<template>
  <v-sheet elevation="0" class="px-8">
    <v-form ref="form">
      <v-card-title>
        <span class="headline">Setup Call Tracking Number</span>
        <v-spacer></v-spacer>
        <v-btn text icon color="grey" @click="onClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text v-if="CallTrackingId == -1">
        <v-row>
          <v-col cols="12" class="px-5">
            <v-radio-group
              v-model="isLocal"
              row
              dense
              @input="updateConfig('isLocal', $event)"
            >
              <v-radio label="Toll Free" :value="false"></v-radio>
              <v-radio label="Local Number" :value="true"></v-radio>
            </v-radio-group>
          </v-col>
          <v-col cols="12" class="px-5">
            <v-text-field
              ref="areaCode"
              label="Area Code"
              placeholder=""
              hint="This is optional"
              persistent-hint
              v-model="areaCode"
              @input="updateConfig('areaCode', $event)"
              class="py-1 pb-4 areaCode"
              append-icon="mdi-open-in-new"
              @click:append="openAreaCodeMap"
              outlined
            >
            </v-text-field>
            <v-btn small outlined @click="getAvailableCallTrackingNumbers"
              >Find Numbers</v-btn
            >
          </v-col>
          <v-col cols="12" class="px-5 phone-number-radios">
            <v-row class="py-0">
              <v-col
                cols="4"
                class="ma-0 pa-0"
                v-for="phoneNumber in phoneNumbers"
                :key="'phonenumber-' + phoneNumber"
              >
                <v-checkbox
                  v-model="phonesToBuy"
                  :value="phoneNumber"
                  :label="phoneNumberFormatter(phoneNumber)"
                  class="my-0"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions v-if="CallTrackingId == -1">
        <v-spacer></v-spacer>
        <v-btn
          class="my-4"
          color="primary"
          @click="onSave"
          :disabled="!phonesToBuy || phonesToBuy.length == 0"
          >{{
            phonesToBuy && phonesToBuy.length > 1
              ? `Buy ${phonesToBuy.length} Numbers`
              : "Buy Number"
          }}</v-btn
        >
      </v-card-actions>
      <v-stepper v-model="stepperStep" alt-labels v-if="CallTrackingId > 0">
        <v-stepper-header>
          <v-stepper-step step="1" editable>Details</v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step step="2" editable>Call Flow</v-stepper-step>
        </v-stepper-header>
        <v-stepper-items>
          <v-stepper-content step="1">
            <v-row>
              <v-col cols="12" class="px-5">
                <v-text-field
                  ref="numberName"
                  label="Name"
                  placeholder=""
                  hint="Choose a brief and memorable name for your tracking number."
                  persistent-hint
                  v-model="numberName"
                  @input="updateConfig('numberName', $event)"
                  class="py-1"
                  required
                  :rules="[rules.required]"
                  outlined
                ></v-text-field>
              </v-col>
              <v-col
                cols="12"
                class="px-5"
                v-if="
                  callTrackingNumber &&
                    callTrackingNumber.meta &&
                    callTrackingNumber.meta.value
                "
              >
                {{ callTrackingNumber.meta.value.display }}
              </v-col>
              <v-col cols="12">
                <v-autocomplete
                  v-model="selectedLeadSource"
                  @change="updateConfig('source', $event)"
                  :items="leadSources.items"
                  item-text="name"
                  item-value="id"
                  label="Select a Lead Source"
                  dense
                  autocomplete="off"
                  outlined
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-stepper-content>

          <v-stepper-content step="2">
            <v-lazy>
              <v-row>
                <v-col cols="12">
                  <v-autocomplete
                    required
                    :rules="[rules.required]"
                    v-model="selectedFlow"
                    @change="onChangeSelectedFlow"
                    :items="callFlows.items"
                    item-text="name"
                    item-value="id"
                    label="Select a Call Flow"
                    dense
                    autocomplete="off"
                    outlined
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-lazy>
            <v-lazy>
              <call-flow-setup
                v-if="!hideFlow"
                :FlowId="flowId"
                :PreviewMode="true"
                :CallTrackingCallback="updateConfig"
                Source="call-tracking-flow"
              ></call-flow-setup>
            </v-lazy>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

      <v-card-actions v-if="CallTrackingId > 0">
        <v-btn
          v-if="stepperStep == 2"
          class="my-4"
          color="primary"
          @click="stepperStep = parseInt(stepperStep) - 1"
          >Back</v-btn
        >
        <v-spacer></v-spacer>
        <v-btn
          v-if="stepperStep == 1"
          class="my-4"
          color="primary"
          @click="stepperStep = parseInt(stepperStep) + 1"
          >Next</v-btn
        >
        <v-btn
          v-if="stepperStep == 2"
          class="my-4"
          color="primary"
          @click="onSave"
          :disabled="!(settings ? settings.isDirty : false)"
          >Save</v-btn
        >
      </v-card-actions>
    </v-form>
  </v-sheet>
</template>

<script>
import ListItem from "../../../models/ListItem";
import { VALIDATION_RULES } from "../../../helpers/ValidationRules";
import { MASKS } from "../../../helpers/Masks";
import { mask } from "vue-the-mask";
import axios from "axios";
import { API_BASE_URL } from "../../../globalVars";
import {
  USPhoneNumberDisplayFormat,
  PhoneNumberCleanFormat
} from "../../../helpers/phoneNumberFormatter";
import CallFlowSetup from "./CallFlowSetup";

export default {
  name: "CallTrackingSetup",
  components: {
    CallFlowSetup
  },
  directives: {
    mask
  },
  props: {
    CallTrackingId: Number,
    CallTrackingNumber: {
      type: Object,
      required: false
    }
  },
  data: function() {
    return {
      dialog: false,
      stepperStep: 1,
      formValid: true,
      editedIndex: -1,
      editedItem: new ListItem(),

      trackingNumberConfig: {},

      numberName: "",
      isLocal: false,
      areaCode: "",
      phoneNumbers: [],
      phonesToBuy: [],
      flowId: null,
      settings: {},

      selectedLeadSource: null,
      selectedFlow: null,
      hideFlow: true,
      masks: MASKS,
      rules: VALIDATION_RULES
    };
  },
  computed: {
    headers() {
      let headers = [
        { text: "Order", value: "rank", sortable: false },
        {
          text: "Name",
          align: "start",
          value: "name",
          sortable: false
        }
      ];
      headers.push({ text: "Action", value: "actions", sortable: false });
      return headers;
    },
    formTitle() {
      return this.editedIndex === -1 ? "Add" : "Edit";
    },
    callTrackingNumber() {
      let vueThis = this;
      let listType = this.$store.getters["entities/listTypes/query"]()
        .withAllRecursive(1)
        .where("code", value => ["call_tracking_number"].includes(value))
        .first();
      if (listType && listType.items && listType.items.length > 0) {
        return listType.items.filter(value => {
          return value.id == vueThis.CallTrackingId;
        })[0];
      } else {
        return null;
      }
    },
    leadSources() {
      let vueThis = this;
      let listType = this.$store.getters["entities/listTypes/query"]()
        .withAllRecursive(1)
        .where("code", value => ["lead_source"].includes(value))
        .first();
      return listType;
    },
    callFlows() {
      const type = this.$store.getters["entities/listTypes/query"]()
        .withAllRecursive(1)
        .where("code", value => ["call_flow"].includes(value))
        .first();
      // type.items.push({
      //   id: -1,
      //   name: " - New Call Flow - "
      // });
      return type;
    }
  },
  created() {
    if (
      this.callTrackingNumber &&
      this.callTrackingNumber.meta &&
      this.callTrackingNumber.meta.value
    ) {
      this.numberName = this.callTrackingNumber.name;
      this.isLocal = this.callTrackingNumber.meta.value.toll_free == false;
      this.settings = JSON.parse(JSON.stringify(this.callTrackingNumber));
      if (this.settings.meta.value.flowId) {
        this.flowId = this.settings.meta.value.flowId;
        this.selectedFlow = this.flowId;
        if (this.selectedFlow > 0) {
          this.hideFlow = false;
        }
      }
      if (this.settings.meta.value.sourceId) {
        this.selectedLeadSource = this.settings.meta.value.sourceId;
      }
    }
  },
  methods: {
    onClose() {
      this.$parent.$parent.$parent.$parent.callTrackingDialog = false;
    },
    onSave() {
      if (this.$refs.form.validate()) {
        let vueThis = this;
        if (this.CallTrackingId > 0) {
          this.settings.id = this.CallTrackingId;
          this.$store.dispatch(
            "entities/listItems/updateListItem",
            vueThis.settings
          );
        } else {
          // this.$store.dispatch(
          //   "entities/listItems/createListItem",
          //   vueThis.settings
          // );
          axios({
            method: "post",
            url: API_BASE_URL + `/account-phone-number?do=buy`,
            data: {
              phoneNumbers: vueThis.phonesToBuy
                .map(val => {
                  return PhoneNumberCleanFormat(val);
                })
                .join(",")
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`
            }
          })
            .then(response => {
              if (response.data == "OK") {
                vueThis.$store.dispatch("entities/listTypes/hydrate", 0);
              } else {
                alert("There was a problem acquiring your tracking number.");
              }
            })
            .catch(error => {
              console.error("Failed to get numbers", error);
            });
        }
        this.onClose();
      }
    },
    updateConfig(field, event) {
      let vueThis = this;
      if (field == "numberName") {
        this.settings.name = event;
        this.$set(this.settings, "name", event);
      } else if (field == "flowId") {
        setTimeout(function() {
          vueThis.settings.meta.value.flowId = event;
          vueThis.flowId = event;
          vueThis.$set(vueThis.settings, "makereactive", Date.now());
        }, 500);
      } else if (field == "source") {
        this.settings.meta.value.sourceId = event;
        this.$set(this.settings, "makereactive", Date.now());
      }
      this.settings.isDirty = true;
    },
    openAreaCodeMap() {
      const url =
        "https://www.nationalnanpa.com/area_code_maps/ac_map_static.html";
      window.open(url, "_blank");
    },
    phoneNumberFormatter(phoneNumber) {
      return USPhoneNumberDisplayFormat(phoneNumber);
    },
    getAvailableCallTrackingNumbers() {
      this.phonesToBuy = [];
      const vueThis = this;
      let queryData = "";
      if (this.isLocal) {
        queryData = `areaCode=${this.areaCode}`;
      } else {
        queryData = `areaCode=${this.areaCode}&isTollFree=true`;
      }
      axios({
        method: "get",
        url: API_BASE_URL + `/account-phone-number?do=search&${queryData}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`
        }
      })
        .then(response => {
          vueThis.phoneNumbers = response.data.data.map(num => {
            return num;
          });
          if (response.data.data.length == 0) {
            alert(
              "There are no available numbers matching your criteria. Try something different."
            );
          }
        })
        .catch(error => {
          console.error("Failed to get numbers", error);
        });
    },
    onChangeSelectedFlow(val) {
      let vueThis = this;
      this.hideFlow = true;
      this.flowId = val;
      this.$nextTick(() => {
        vueThis.hideFlow = false;
        vueThis.updateConfig("flowId", val);
      });
    }
  }
};
</script>

<style scoped lang="stylus">
div.areaCode {
  max-width: 300px;
}
div.phone-number-radios {
  overflow-x: hidden;
  overflow-y: scroll;
  max-height: 225px
}
</style>
